import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import { Progress } from '../components/ui/progress';
import { 
  Gift, 
  Copy, 
  Check, 
  Users, 
  DollarSign,
  Clock,
  Share2,
  ChevronRight,
  Mail,
  MessageCircle,
  Twitter,
  LogOut,
  User,
  Settings,
  Trophy,
  Crown,
  Medal,
  Award,
  Star
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const ReferralPage = () => {
  const { user, logout, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [statsRes, historyRes, leaderboardRes, badgesRes] = await Promise.all([
        api.get('/referrals/stats'),
        api.get('/referrals/history'),
        api.get('/referrals/leaderboard'),
        api.get('/referrals/badges')
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
      setLeaderboard(leaderboardRes.data);
      setBadges(badgesRes.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const getBadgeIcon = (iconName) => {
    const icons = {
      star: <Star className="w-5 h-5" />,
      trophy: <Trophy className="w-5 h-5" />,
      crown: <Crown className="w-5 h-5" />,
      medal: <Medal className="w-5 h-5" />,
      award: <Award className="w-5 h-5" />
    };
    return icons[iconName] || <Star className="w-5 h-5" />;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-zinc-500 font-bold">#{rank}</span>;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const shareVia = (platform) => {
    if (!stats) return;
    const message = encodeURIComponent(`Join me on Lagree MVMT and get $50 credit! Use my code: ${stats.referral_code}`);
    const link = encodeURIComponent(stats.share_link);
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${message}&url=${link}`,
      email: `mailto:?subject=${encodeURIComponent('Join Lagree MVMT!')}&body=${message}%0A%0A${link}`,
      sms: `sms:?body=${message}%20${link}`
    };
    
    if (urls[platform]) {
      window.open(urls[platform], '_blank');
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-zinc-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <PageTitle title="Refer a Friend" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/dashboard" size="default" />

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">Home</Link>
            <Link to="/classes" className="text-zinc-400 hover:text-white transition-colors">Classes</Link>
            <Link to="/programs" className="text-zinc-400 hover:text-white transition-colors">Programs</Link>
            <Link to="/shop" className="text-zinc-400 hover:text-white transition-colors">Shop</Link>
            <Link to="/instructors" className="text-zinc-400 hover:text-white transition-colors">Instructors</Link>
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <div className="w-8 h-8 bg-teal-400 rounded-full flex items-center justify-center text-sm font-bold">
                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                </div>
                <span className="hidden md:inline text-zinc-300">{user?.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-zinc-800">
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                  <User className="w-4 h-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-400 cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-400/20 rounded-full mb-6">
            <Gift className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">REFER A FRIEND</h1>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Share Lagree MVMT with friends and earn $50 credit for each friend who subscribes. 
            They'll get $50 off too!
          </p>
        </div>

        {/* Referral Code Card */}
        <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-lg p-8 mb-8">
          <div className="text-center">
            <p className="text-zinc-400 uppercase text-sm tracking-wider mb-2">Your Referral Code</p>
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-4xl font-bold tracking-widest text-white" data-testid="referral-code">
                {stats?.referral_code || '---'}
              </span>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => copyToClipboard(stats?.referral_code)}
                className="hover:bg-teal-500/20"
                data-testid="copy-code-btn"
              >
                {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-teal-400" />}
              </Button>
            </div>
            
            <p className="text-zinc-500 text-sm mb-6">Or share your personal link:</p>
            <div className="flex items-center gap-2 bg-[#050505]/50 rounded-lg p-3 max-w-lg mx-auto">
              <input 
                type="text" 
                readOnly 
                value={stats?.share_link || ''}
                className="flex-1 bg-transparent text-zinc-300 text-sm outline-none"
              />
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => copyToClipboard(stats?.share_link)}
                className="hover:bg-teal-500/20 text-teal-400"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
          <Button 
            onClick={() => shareVia('email')}
            className="bg-[#121212] border border-zinc-800 hover:border-zinc-700"
            data-testid="share-email"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email
          </Button>
          <Button 
            onClick={() => shareVia('sms')}
            className="bg-[#121212] border border-zinc-800 hover:border-zinc-700"
            data-testid="share-sms"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Text
          </Button>
          <Button 
            onClick={() => shareVia('twitter')}
            className="bg-[#121212] border border-zinc-800 hover:border-zinc-700"
            data-testid="share-twitter"
          >
            <Twitter className="w-4 h-4 mr-2" />
            Twitter
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5 text-center">
            <Users className="w-6 h-6 text-teal-400 mx-auto mb-2" />
            <p className="text-3xl font-bold">{stats?.total_referrals || 0}</p>
            <p className="text-zinc-500 text-sm">Total Invited</p>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5 text-center">
            <Check className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-green-400">{stats?.successful_referrals || 0}</p>
            <p className="text-zinc-500 text-sm">Subscribed</p>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5 text-center">
            <Clock className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-yellow-400">{stats?.pending_referrals || 0}</p>
            <p className="text-zinc-500 text-sm">Pending</p>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5 text-center">
            <DollarSign className="w-6 h-6 text-teal-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-teal-400">${stats?.available_credits?.toFixed(2) || '0.00'}</p>
            <p className="text-zinc-500 text-sm">Available Credit</p>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-[#121212] border border-zinc-800 rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-bold mb-6 text-center">HOW IT WORKS</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Share2 className="w-6 h-6 text-teal-400" />
              </div>
              <h3 className="font-semibold mb-2">1. Share Your Code</h3>
              <p className="text-zinc-500 text-sm">Send your unique referral code or link to friends</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-teal-400" />
              </div>
              <h3 className="font-semibold mb-2">2. Friend Joins</h3>
              <p className="text-zinc-500 text-sm">They sign up using your code and start their free trial</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-teal-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-6 h-6 text-teal-400" />
              </div>
              <h3 className="font-semibold mb-2">3. You Both Earn</h3>
              <p className="text-zinc-500 text-sm">When they subscribe, you both get $50 credit!</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-teal-400 text-black' : 'text-zinc-400'}
          >
            Overview
          </Button>
          <Button
            variant={activeTab === 'leaderboard' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('leaderboard')}
            className={activeTab === 'leaderboard' ? 'bg-teal-400 text-black' : 'text-zinc-400'}
          >
            <Trophy className="w-4 h-4 mr-2" />
            Leaderboard
          </Button>
          <Button
            variant={activeTab === 'badges' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('badges')}
            className={activeTab === 'badges' ? 'bg-teal-400 text-black' : 'text-zinc-400'}
          >
            <Award className="w-4 h-4 mr-2" />
            Badges
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Referral History */}
            {history.length > 0 && (
              <div className="bg-[#121212] border border-zinc-800 rounded-lg p-6">
                <h2 className="text-xl font-bold mb-4">Referral History</h2>
                <div className="space-y-3">
                  {history.map((referral) => (
                    <div 
                      key={referral.id} 
                      className="flex items-center justify-between p-4 bg-[#050505] rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-zinc-500" />
                        </div>
                        <div>
                          <p className="font-medium">{referral.referred_name}</p>
                          <p className="text-zinc-500 text-sm">{referral.referred_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                          referral.status === 'completed' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {referral.status === 'completed' ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              +${referral.credit_earned}
                            </>
                          ) : (
                            <>
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {history.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">No referrals yet</p>
                <p className="text-sm">Share your code and start earning credits!</p>
              </div>
            )}
          </>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="bg-[#121212] border border-zinc-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Top Referrers</h2>
              <span className="text-zinc-500 text-sm">This Month</span>
            </div>
            
            {leaderboard.length > 0 ? (
              <div className="space-y-3">
                {leaderboard.map((entry, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-4 rounded-lg ${
                      index < 3 ? 'bg-gradient-to-r from-teal-500/10 to-transparent border border-teal-500/20' : 'bg-[#050505]'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center">
                        {getRankBadge(entry.rank)}
                      </div>
                      <div>
                        <p className="font-semibold">{entry.name}</p>
                        <div className="flex items-center gap-2">
                          {entry.badges?.slice(0, 3).map((badge, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-teal-500/20 text-teal-400 rounded">
                              {badge.replace('referral_', '').replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-teal-400">{entry.successful_referrals}</p>
                      <p className="text-zinc-500 text-xs">referrals</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">No rankings yet</p>
                <p className="text-sm">Be the first to complete a referral!</p>
              </div>
            )}
          </div>
        )}

        {/* Badges Tab */}
        {activeTab === 'badges' && badges && (
          <div className="space-y-6">
            {/* Next Badge Progress */}
            {badges.next_badge && (
              <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-zinc-400 text-sm uppercase tracking-wider">Next Badge</p>
                    <h3 className="text-xl font-bold">{badges.next_badge.name}</h3>
                    <p className="text-zinc-500 text-sm">{badges.next_badge.description}</p>
                  </div>
                  <div className="w-16 h-16 bg-teal-400/20 rounded-full flex items-center justify-center text-teal-400">
                    {getBadgeIcon(badges.next_badge.icon)}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">{badges.successful_referrals} / {badges.next_badge.requirement} referrals</span>
                    <span className="text-teal-400">{badges.next_badge.referrals_needed} more needed</span>
                  </div>
                  <Progress value={(badges.successful_referrals / badges.next_badge.requirement) * 100} className="h-2" />
                </div>
              </div>
            )}

            {/* All Badges */}
            <div className="bg-[#121212] border border-zinc-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Your Badges</h2>
                <span className="text-teal-400 text-sm">{badges.total_earned} / {badges.badges.length} earned</span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {badges.badges.map((badge) => (
                  <div 
                    key={badge.id}
                    className={`relative p-4 rounded-lg text-center transition-all ${
                      badge.earned 
                        ? 'bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/30' 
                        : 'bg-[#050505] border border-zinc-800 opacity-50'
                    }`}
                  >
                    <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                      badge.earned ? 'bg-teal-400 text-black' : 'bg-zinc-800 text-zinc-500'
                    }`}>
                      {getBadgeIcon(badge.icon)}
                    </div>
                    <h4 className="font-semibold text-sm mb-1">{badge.name}</h4>
                    <p className="text-zinc-500 text-xs">{badge.requirement} referrals</p>
                    {badge.earned && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReferralPage;
